import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Clock, Bell, Package, CalendarDays,
  BellOff, Loader2, Trash2, Pencil, Check, X,
} from 'lucide-react';
import { useMyStockAlerts } from '../../hooks/useMyStockAlerts';
import { ProductThumb } from '../../components/product/ProductThumb';
import { toast } from 'sonner';

/** Campo quantità cliccabile per modificare `qtyWanted` di un avviso
 * attivo (richiesta utente 24/02/2026): mostra il numero, al click si
 * apre in editing con conferma (spunta) o annulla (X). */
function EditableQty({ sku, value, onSave }: { sku: string; value: number; onSave: (qty: number) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  if (!editing) {
    return (
      <button
        onClick={() => { setDraft(value); setEditing(true); }}
        className="inline-flex items-center gap-1 font-medium hover:text-[hsl(214,58%,41%)] transition-colors group"
        title="Modifica quantità"
        data-testid={`avviso-qty-edit-${sku}`}
      >
        {value}
        <Pencil className="w-3 h-3 text-muted-foreground group-hover:text-[hsl(214,58%,41%)]" />
      </button>
    );
  }

  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      toast.error('Errore durante il salvataggio della quantità');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="number"
        min={1}
        value={draft}
        onChange={e => setDraft(Math.max(1, parseInt(e.target.value, 10) || 1))}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
        disabled={saving}
        className="w-14 h-7 px-1.5 text-center text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)] disabled:opacity-60"
        data-testid={`avviso-qty-input-${sku}`}
      />
      <button
        onClick={save}
        disabled={saving}
        className="text-emerald-600 hover:text-emerald-700 p-1 rounded disabled:opacity-50"
        title="Salva"
        data-testid={`avviso-qty-save-${sku}`}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={() => setEditing(false)}
        disabled={saving}
        className="text-muted-foreground hover:text-rose-600 p-1 rounded disabled:opacity-50"
        title="Annulla"
        data-testid={`avviso-qty-cancel-${sku}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function WaitingListPage() {
  const { alerts, loading, removeAlert, updateQty } = useMyStockAlerts();

  const active    = alerts.filter(a => a.status === 'active');
  const fulfilled = alerts.filter(a => a.status === 'fulfilled');

  const handleRemove = async (sku: string, title: string) => {
    try {
      await removeAlert(sku);
      toast.success(`Avviso rimosso per ${title}`);
    } catch {
      toast.error('Errore durante la rimozione');
    }
  };

  return (
    <div data-testid="waiting-list-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-[hsl(214,58%,41%)]" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Avvisi di Disponibilità</h1>
          <p className="text-sm text-muted-foreground">
            {loading ? 'Caricamento…' : `${active.length} ${active.length === 1 ? 'avviso attivo' : 'avvisi attivi'}`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="avvisi-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Caricamento avvisi…
        </div>
      ) : alerts.length === 0 ? (
        <div className="rounded-xl border bg-white p-12 flex flex-col items-center text-center gap-3" data-testid="avvisi-empty">
          <BellOff className="w-10 h-10 text-muted-foreground/40" />
          <p className="font-medium">Nessun avviso attivato</p>
          <p className="text-sm text-muted-foreground max-w-xs">
            Quando un articolo è esaurito, puoi attivare un avviso dalla scheda prodotto. Riceverai una notifica appena torna disponibile.
          </p>
          <Link
            to="/"
            className="mt-2 text-sm font-medium text-[hsl(214,58%,46%)] hover:underline"
          >
            Sfoglia il catalogo
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Avvisi attivi */}
          {active.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                In attesa ({active.length})
              </h2>
              <div className="rounded-xl border bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="avvisi-active-table">
                  <thead>
                    <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                      <th className="w-14 px-4 py-3" />
                      <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                      <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase hidden sm:table-cell">Marca</th>
                      <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Qtà</th>
                      <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Arrivo previsto</th>
                      <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Richiesto il</th>
                      <th className="w-12 px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {active.map(alert => (
                      <tr key={alert.sku} className="border-b last:border-b-0 hover:bg-secondary/40" data-testid={`avviso-row-${alert.sku}`}>
                        <td className="px-4 py-3">
                          <ProductThumb sku={alert.sku} brand={alert.brand} size="sm" />
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/prodotti/${alert.category}/${alert.sku}`}
                            className="font-medium hover:text-[hsl(214,58%,41%)]"
                          >
                            {alert.title}
                          </Link>
                          <div className="text-[10px] font-mono text-muted-foreground">{alert.sku}</div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs px-2 py-0.5 rounded border bg-secondary/60">{alert.brand}</span>
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          <EditableQty sku={alert.sku} value={alert.qtyWanted} onSave={(qty) => updateQty(alert.sku, qty)} />
                        </td>
                        <td className="px-4 py-3 text-center text-xs" data-testid={`avviso-arrivo-${alert.sku}`}>
                          {alert.prossimArrivo ? (
                            <>
                              <div className="font-medium">{new Date(alert.prossimArrivo.slice(0, 10) + 'T00:00:00').toLocaleDateString('it-IT')}</div>
                              {alert.qtaArrivo != null && alert.qtaArrivo > 0 && (
                                <div className="text-muted-foreground">{alert.qtaArrivo} pz in arrivo</div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">Non prevista</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-muted-foreground text-xs">
                          {new Date(alert.createdAt).toLocaleDateString('it-IT')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleRemove(alert.sku, alert.title)}
                            className="text-muted-foreground hover:text-rose-600 transition-colors p-1 rounded"
                            title="Rimuovi avviso"
                            data-testid={`avviso-remove-${alert.sku}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Avvisi evasi */}
          {fulfilled.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-emerald-600" />
                Già disponibili ({fulfilled.length})
              </h2>
              <div className="rounded-xl border bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="avvisi-fulfilled-table">
                  <thead>
                    <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                      <th className="w-14 px-4 py-3" />
                      <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                      <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase hidden sm:table-cell">Marca</th>
                      <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Disponibile dal</th>
                      <th className="w-12 px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {fulfilled.map(alert => (
                      <tr key={alert.sku} className="border-b last:border-b-0 hover:bg-secondary/40 opacity-70" data-testid={`avviso-fulfilled-${alert.sku}`}>
                        <td className="px-4 py-3">
                          <ProductThumb sku={alert.sku} brand={alert.brand} size="sm" />
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/prodotti/${alert.category}/${alert.sku}`}
                            className="font-medium hover:text-[hsl(214,58%,41%)]"
                          >
                            {alert.title}
                          </Link>
                          <div className="text-[10px] font-mono text-muted-foreground">{alert.sku}</div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs px-2 py-0.5 rounded border bg-secondary/60">{alert.brand}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-medium">
                            {alert.fulfilledAt ? new Date(alert.fulfilledAt).toLocaleDateString('it-IT') : 'Disponibile'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleRemove(alert.sku, alert.title)}
                            className="text-muted-foreground hover:text-rose-600 transition-colors p-1 rounded"
                            title="Rimuovi"
                            data-testid={`avviso-fulfilled-remove-${alert.sku}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
